// Ground radar (A-SMGCS-like surface movement display): aerodrome geometry from OpenStreetMap, stop bars,
// aircraft as scaled outlines with labels. Targets update once per second like a real surface tracker.
import type { GameSession } from '../../game/session';
import type { Aircraft } from '../../sim/aircraft';
import { View, attachPanZoom, silhouette } from './view';
import { C } from './colors';
import { DEG, bearing } from '../../sim/geo';

interface Sample { x: number; y: number; hdg: number }

export class GroundRadar {
  readonly view = new View();
  private readonly ctx: CanvasRenderingContext2D;
  private paths: Record<string, Path2D> = {};
  private twyLabels: { x: number; y: number; name: string }[] = [];
  private samples = new Map<string, Sample>();
  private sampleT = -1;
  private hits: { cs: string; x: number; y: number; r: number; lx: number; ly: number; lw: number; lh: number }[] = [];
  private detach: () => void;
  routePreview: number[] | null = null;
  private bounds = { x0: -1000, x1: 1000, y0: -1000, y1: 1000 };
  private autoFit = true;
  private fitView(): void {
    const b = this.bounds, v = this.view;
    v.cx = (b.x0 + b.x1) / 2; v.cy = (b.y0 + b.y1) / 2;
    v.scale = Math.min(v.w / (b.x1 - b.x0 + 250), v.h / (b.y1 - b.y0 + 250));
  }
  onSelect: (cs: string | null) => void = () => {};
  onContext: (cs: string | null, sx: number, sy: number) => void = () => {};

  constructor(private readonly canvas: HTMLCanvasElement, private readonly s: GameSession) {
    this.ctx = canvas.getContext('2d')!;
    this.build();
    // initial view: whole runway system
    const ap = s.airport;
    // runway system and aprons (stands) define the initial view
    const pts = [...ap.runways.flatMap(r => [r.ends[r.endIds[0]]!.start, r.ends[r.endIds[1]]!.start]), ...Object.values(ap.stands).map(s => ap.nodes[s.node]!)];
    const xs = pts.map(n => n.x), ys = pts.map(n => n.y);
    this.bounds = { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) };
    this.detach = attachPanZoom(canvas, this.view, {
      min: 0.05, max: 6,
      onInteract: () => { this.autoFit = false; },
      onClick: (x, y) => this.onSelect(this.pick(x, y)),
      onContext: (x, y) => this.onContext(this.pick(x, y), x, y),
    });
  }
  dispose(): void { this.detach(); }

  private build(): void {
    const geo = this.s.airport.geo;
    const poly = (pts: [number, number][], close = true) => {
      const p = new Path2D();
      pts.forEach(([x, y], i) => (i ? p.lineTo(x, y) : p.moveTo(x, y)));
      if (close) p.closePath();
      return p;
    };
    const boundary = new Path2D();
    for (const b of geo.draw.boundary) boundary.addPath(poly(b));
    this.paths.boundary = boundary;
    const aprons = new Path2D();
    for (const a of geo.draw.aprons) aprons.addPath(poly(a));
    this.paths.aprons = aprons;
    const buildings = new Path2D(), terminals = new Path2D();
    for (const b of geo.draw.buildings) (b.kind === 'terminal' ? terminals : buildings).addPath(poly(b.pts));
    this.paths.buildings = buildings;
    this.paths.terminals = terminals;
    // taxiways grouped by width
    for (const l of geo.draw.lines) {
      const key = `${l.kind}|${Math.round(l.width)}`;
      const p = (this.paths[key] ??= new Path2D());
      p.addPath(poly(l.pts, false));
    }
    // taxiway name labels: one per ~350 m of each named taxiway
    const placed: { x: number; y: number; name: string }[] = [];
    for (const l of geo.draw.lines) {
      if ((l.kind !== 'twy' && l.kind !== 'lane') || !l.name || l.pts.length < 2) continue;
      for (let i = 0; i < l.pts.length - 1; i++) {
        const [ax, ay] = l.pts[i]!, [bx, by] = l.pts[i + 1]!;
        if (Math.hypot(bx - ax, by - ay) < 60) continue;
        const m = { x: (ax + bx) / 2, y: (ay + by) / 2, name: l.name };
        if (placed.some(o => o.name === m.name && Math.hypot(o.x - m.x, o.y - m.y) < 350)) continue;
        placed.push(m);
      }
    }
    this.twyLabels = placed;
  }

  private sample(): void {
    const t = Math.floor(this.s.world.t);
    if (t === this.sampleT) return;
    this.sampleT = t;
    this.samples.clear();
    for (const a of this.s.world.aircraft) if (a.onGround || a.altFt - this.s.airport.meta.elevFt < 400) this.samples.set(a.cs, { x: a.x, y: a.y, hdg: a.hdg });
  }

  draw(): void {
    const { ctx, view } = this;
    if (view.fit(this.canvas) && this.autoFit) this.fitView();
    this.sample();
    const dpr = view.dpr, s = view.scale;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, view.w, view.h);
    // world transform: metres, y up
    ctx.setTransform(s * dpr, 0, 0, -s * dpr, (view.w / 2 - view.cx * s) * dpr, (view.h / 2 + view.cy * s) * dpr);
    ctx.fillStyle = C.airfield; ctx.fill(this.paths.boundary!);
    ctx.fillStyle = C.apron; ctx.fill(this.paths.aprons!);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const [key, p] of Object.entries(this.paths)) {
      const [kind, w] = key.split('|');
      if (!w) continue;
      if (kind === 'twy' || kind === 'lane' || kind === 'stand') {
        ctx.strokeStyle = kind === 'stand' ? C.apron : kind === 'lane' ? C.taxilane : C.taxiway;
        ctx.lineWidth = kind === 'stand' ? 2 : Math.max(Number(w), 1.5 / s);
        ctx.stroke(p);
      }
    }
    for (const [key, p] of Object.entries(this.paths)) {
      const [kind, w] = key.split('|');
      if (kind !== 'rwy' && kind !== 'displaced' && kind !== 'closed') continue;
      ctx.lineCap = 'butt';
      ctx.strokeStyle = kind === 'closed' ? C.taxilane : C.runwayEdge; ctx.lineWidth = Number(w) + 2 / s; ctx.stroke(p);
      ctx.strokeStyle = kind === 'rwy' ? C.runway : '#1d2225'; ctx.lineWidth = Number(w); ctx.stroke(p);
    }
    ctx.lineCap = 'round';
    // taxiway centre lines
    if (s > 0.25) {
      ctx.strokeStyle = C.centreline; ctx.lineWidth = Math.max(0.3, 0.9 / s) ; ctx.globalAlpha = 0.7;
      for (const [key, p] of Object.entries(this.paths)) { const [kind, w] = key.split('|'); if (w && (kind === 'twy' || kind === 'lane')) ctx.stroke(p); }
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = C.building; ctx.fill(this.paths.buildings!);
    ctx.fillStyle = C.terminal; ctx.fill(this.paths.terminals!);
    ctx.strokeStyle = C.buildingEdge; ctx.lineWidth = 1 / s; ctx.stroke(this.paths.buildings!); ctx.stroke(this.paths.terminals!);
    this.drawRunwayMarkings();
    this.drawHolds();
    if (this.routePreview) this.drawRoute(this.routePreview);
    // aircraft outlines
    this.hits = [];
    for (const a of this.s.world.aircraft) {
      const sm = this.samples.get(a.cs);
      if (!sm) continue;
      this.drawAircraft(a, sm);
    }
    // screen-space overlays
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawLabels();
    this.drawTaxiwayNames();
    this.drawScale();
  }

  private drawRunwayMarkings(): void {
    const { ctx, view } = this;
    const ap = this.s.airport;
    for (const rw of ap.runways) {
      const a = rw.ends[rw.endIds[0]]!, b = rw.ends[rw.endIds[1]]!;
      // dashed centre line
      ctx.save();
      ctx.strokeStyle = C.marking; ctx.lineWidth = 0.9; ctx.setLineDash([30, 20]); ctx.globalAlpha = 0.8;
      ctx.beginPath(); ctx.moveTo(a.thr.x, a.thr.y); ctx.lineTo(b.thr.x, b.thr.y); ctx.stroke();
      ctx.restore();
      // threshold bars
      for (const e of [a, b]) {
        const ux = Math.sin(e.hdg * DEG), uy = Math.cos(e.hdg * DEG), nx = uy, ny = -ux;
        ctx.strokeStyle = C.marking; ctx.lineWidth = 1.8; ctx.globalAlpha = 0.85;
        for (let k = -7; k <= 7; k++) {
          if (Math.abs(k) < 1) continue;
          const off = k * (rw.width / 16);
          ctx.beginPath(); ctx.moveTo(e.thr.x + nx * off + ux * 6, e.thr.y + ny * off + uy * 6); ctx.lineTo(e.thr.x + nx * off + ux * 36, e.thr.y + ny * off + uy * 36); ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }
    }
    // runway designators in screen space
    ctx.save();
    ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
    ctx.font = '600 12px "IBM Plex Mono", monospace'; ctx.fillStyle = C.marking; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const e of Object.values(ap.runwayEnds)) {
      const p = { x: e.thr.x - Math.sin(e.hdg * DEG) * 70, y: e.thr.y - Math.cos(e.hdg * DEG) * 70 };
      const [x, y] = view.toScreen(p.x, p.y);
      ctx.fillText(e.id, x, y);
    }
    ctx.restore();
  }

  private drawHolds(): void {
    const { ctx } = this;
    const w = this.s.world, ap = this.s.airport;
    const activeRwys = new Set([...w.config.dep, ...w.config.arr].map(e => ap.runwayEnds[e]?.runway));
    for (const h of ap.holds) {
      if (h.type === 'intermediate') continue;
      const n = ap.nodes[h.node]!;
      // orientation: perpendicular to the taxiway at the node
      const nb = ap.adj[h.node]!.find(l => l.e.kind !== 'stand');
      if (!nb) continue;
      const o = ap.nodes[nb.to]!;
      const brg = bearing(n.x, n.y, o.x, o.y) + 90;
      const half = h.type === 'runway' ? 14 : 11;
      const dx = Math.sin(brg * DEG) * half, dy = Math.cos(brg * DEG) * half;
      const lit = h.type === 'runway' && h.runway && (activeRwys.has(h.runway) || w.lvp) && !w.aircraft.some(a => a.onGround && Math.hypot(a.x - n.x, a.y - n.y) < 40 && w.pilot(a.cs)?.crossings.size);
      ctx.strokeStyle = h.type === 'ILS' ? (w.lvp ? C.stopbar : C.holdline) : lit ? C.stopbar : C.holdline;
      ctx.lineWidth = h.type === 'ILS' ? 1.4 : 2.6;
      ctx.globalAlpha = h.type === 'ILS' && !w.lvp ? 0.45 : 0.95;
      ctx.beginPath(); ctx.moveTo(n.x - dx, n.y - dy); ctx.lineTo(n.x + dx, n.y + dy); ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private drawRoute(path: number[]): void {
    const { ctx } = this;
    const ap = this.s.airport;
    ctx.strokeStyle = C.route; ctx.lineWidth = 3.5; ctx.globalAlpha = 0.75; ctx.setLineDash([12, 8]);
    ctx.beginPath();
    path.forEach((id, i) => { const n = ap.nodes[id]!; if (i) ctx.lineTo(n.x, n.y); else ctx.moveTo(n.x, n.y); });
    ctx.stroke();
    ctx.setLineDash([]); ctx.globalAlpha = 1;
  }

  private colourOf(a: Aircraft): string {
    const w = this.s.world;
    if (this.s.selected === a.cs) return C.selected;
    const p = w.pilot(a.cs);
    const mine = !!p?.freq && w.playerUnits().includes(p.freq);
    if (mine && w.requests.some(r => r.cs === a.cs && w.playerUnits().includes(r.unit))) return C.calling;
    if (mine) return p!.isDeparture ? C.dep : C.arr;
    return C.other;
  }

  private drawAircraft(a: Aircraft, sm: Sample): void {
    const { ctx } = this;
    const t = a.type;
    const pts = silhouette(t.lengthM, t.spanM, t.fuselageM, t.tail, t.engine);
    ctx.save();
    ctx.translate(sm.x, sm.y);
    ctx.rotate(-sm.hdg * DEG);
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    const col = this.colourOf(a);
    ctx.fillStyle = col; ctx.globalAlpha = 0.85; ctx.fill();
    ctx.globalAlpha = 1; ctx.strokeStyle = '#0b0e10'; ctx.lineWidth = 0.6; ctx.stroke();
    ctx.restore();
    // wait indicator (holding short / traffic) is part of the label
    const [sx, sy] = this.view.toScreen(sm.x - Math.sin(sm.hdg * DEG) * t.lengthM * 0.45, sm.y - Math.cos(sm.hdg * DEG) * t.lengthM * 0.45);
    this.hits.push({ cs: a.cs, x: sx, y: sy, r: Math.max(10, t.lengthM * this.view.scale * 0.6), lx: 0, ly: 0, lw: 0, lh: 0 });
  }

  private drawLabels(): void {
    const { ctx } = this;
    const w = this.s.world;
    ctx.font = '500 11px "IBM Plex Mono", monospace';
    ctx.textBaseline = 'top';
    const placed: { x: number; y: number; w: number; h: number }[] = [];
    for (const h of this.hits) {
      const a = w.ac(h.cs);
      if (!a) continue;
      const p = w.pilot(a.cs);
      const col = this.colourOf(a);
      const l1 = a.cs;
      const target = p?.isDeparture ? (p.depHold ? `${p.depHold.name}·${p.depRwy}` : p.clearance ? 'CLR' : '') : a.fp.stand ? `P${a.fp.stand}` : '';
      const l2 = `${a.type.icao}${a.type.wake === 'H' || a.type.wake === 'J' ? ' ' + a.type.wake : ''} ${target}`.trim();
      const wait = a.holdPosition ? 'HOLD' : a.waitingAt()?.startsWith('RWY') ? 'RWY' : a.trafficCs ? 'TFC' : '';
      const lw = Math.max(ctx.measureText(l1).width, ctx.measureText(l2).width) + 8, lh = 28;
      // simple label de-cluttering: try positions around the target until one is free
      const cand = [[14, -30], [14, 6], [-lw - 14, -30], [-lw - 14, 6], [14, -62], [-lw - 14, -62], [14, 38]] as const;
      let lx = h.x + 14, ly = h.y - 30;
      for (const [dx, dy] of cand) {
        const x = h.x + dx, y = h.y + dy;
        if (!placed.some(o => x < o.x + o.w + 2 && x + lw + 2 > o.x && y < o.y + o.h + 2 && y + lh + 2 > o.y)) { lx = x; ly = y; break; }
      }
      placed.push({ x: lx, y: ly, w: lw, h: lh });
      ctx.strokeStyle = col; ctx.globalAlpha = 0.5; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(h.x, h.y); ctx.lineTo(lx < h.x ? lx + lw : lx, ly < h.y ? ly + lh : ly); ctx.stroke(); ctx.globalAlpha = 1;
      if (this.s.selected === a.cs) { ctx.strokeStyle = C.selected; ctx.strokeRect(lx - 1, ly - 1, lw + 2, lh + 2); }
      ctx.fillStyle = 'rgba(16,20,23,0.72)'; ctx.fillRect(lx, ly, lw, lh);
      ctx.fillStyle = col; ctx.fillText(l1, lx + 4, ly + 2);
      ctx.fillStyle = col === C.other ? C.other : '#aeb9c1'; ctx.fillText(l2, lx + 4, ly + 15);
      if (wait) { ctx.fillStyle = C.warn; ctx.fillText(wait, lx + lw + 4, ly + 2); }
      h.lx = lx; h.ly = ly; h.lw = lw; h.lh = lh;
    }
  }

  private drawTaxiwayNames(): void {
    if (this.view.scale < 0.18) return;
    const { ctx, view } = this;
    ctx.font = '600 10px "IBM Plex Mono", monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const l of this.twyLabels) {
      const [x, y] = view.toScreen(l.x, l.y);
      if (x < -20 || y < -20 || x > view.w + 20 || y > view.h + 20) continue;
      const tw = ctx.measureText(l.name).width + 6;
      ctx.fillStyle = '#121618'; ctx.fillRect(x - tw / 2, y - 7, tw, 14);
      ctx.fillStyle = '#d9b84c'; ctx.fillText(l.name, x, y + 0.5);
    }
    if (view.scale > 0.6) {
      ctx.font = '500 9px "IBM Plex Mono", monospace'; ctx.fillStyle = C.label;
      for (const st of Object.values(this.s.airport.stands)) {
        const n = this.s.airport.nodes[st.node]!;
        const [x, y] = view.toScreen(n.x, n.y);
        ctx.fillText(st.id, x, y);
      }
    }
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  }

  private drawScale(): void {
    const { ctx, view } = this;
    const m = [50, 100, 200, 500, 1000].find(v => v * view.scale > 70) ?? 1000;
    const px = m * view.scale;
    ctx.strokeStyle = C.mapText; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(14, view.h - 14); ctx.lineTo(14 + px, view.h - 14); ctx.moveTo(14, view.h - 18); ctx.lineTo(14, view.h - 10); ctx.moveTo(14 + px, view.h - 18); ctx.lineTo(14 + px, view.h - 10); ctx.stroke();
    ctx.fillStyle = C.mapText; ctx.font = '10px "IBM Plex Mono", monospace'; ctx.fillText(`${m} m`, 18 + px, view.h - 10);
  }

  pick(sx: number, sy: number): string | null {
    let best: string | null = null, bd = Infinity;
    for (const h of this.hits) {
      if (sx >= h.lx && sx <= h.lx + h.lw && sy >= h.ly && sy <= h.ly + h.lh) return h.cs;
      const d = Math.hypot(sx - h.x, sy - h.y);
      if (d < h.r + 6 && d < bd) { bd = d; best = h.cs; }
    }
    return best;
  }
  centreOn(cs: string): void {
    const a = this.s.world.ac(cs);
    if (!a) return;
    this.view.cx = a.x; this.view.cy = a.y; this.view.version++;
  }
}
